import axios from 'axios';
const axiosInstance = axios.create({});

// helper: make small, non-circular previews (now supports FormData & JSON safely)
function summarize(val) {
    try {
        if (!val) return undefined;

        // --- FormData ---
        if (typeof FormData !== 'undefined' && val instanceof FormData) {
            const obj = {};
            for (const [k, v] of val.entries()) {
                if (v instanceof File || v instanceof Blob) {
                    const name = v.name || 'blob';
                    const size = typeof v.size === 'number' ? v.size : '?';
                    obj[k] = `[File: ${name}, ${size} bytes]`;
                } else {
                    obj[k] = String(v).slice(0, 200);
                }
            }
            return obj;
        }

        // --- Plain string ---
        if (typeof val === 'string') return val.slice(0, 1000);

        // --- Regular objects ---
        if (typeof val === 'object') {
            const copy = {};
            for (const [k, v] of Object.entries(val)) {
                if (['string', 'number', 'boolean'].includes(typeof v)) {
                    copy[k] = v;
                } else if (Array.isArray(v)) {
                    copy[k] = `[Array(${v.length})]`;
                } else if (v && typeof v === 'object') {
                    copy[k] = '[object]';
                }
            }
            // limit JSON length to avoid log explosion
            return JSON.parse(JSON.stringify(copy, null, 2).slice(0, 2000));
        }
    } catch {
        return '[unserializable]';
    }
}

// simple string clamp
function clampStr(str, n = 1000) {
    if (typeof str !== 'string') return str;
    return str.length > n ? str.slice(0, n) + '…[truncated]' : str;
}

// redact sensitive headers
function summarizeHeaders(headers) {
    if (!headers || typeof headers !== 'object') return headers;
    const safe = {};
    const REDACT = ['authorization', 'cookie', 'x-api-key'];
    for (const [k, v] of Object.entries(headers)) {
        if (REDACT.includes(k.toLowerCase())) safe[k] = '[redacted]';
        else safe[k] = typeof v === 'string' ? clampStr(v, 300) : v;
    }
    return safe;
}


// as-is (add auth headers here if you need)
axiosInstance.interceptors.request.use(
    config => config,
    error => Promise.reject(error)
);

// --- RESPONSE ERROR INTERCEPTOR ---
axiosInstance.interceptors.response.use(
    res => { // Stop loader if active

        if (typeof window !== 'undefined' && window.$loading) {
            window.$loading.finish();
        }
        return res;
    },
    err => {

        try {
            // Stop loader if active
            if (typeof window !== 'undefined' && window.$loading) {
                window.$loading.finish();
            }

            // de-dupe: if already logged somewhere, skip
            if (err.__clientLogged) return Promise.reject(err);

            const logError = (typeof window !== 'undefined') && window.$logError;
            if (!logError) return Promise.reject(err);

            const res = err.response;
            const cfg = err.config || {};
            const headers = res?.headers || {};

            // allow per-call opt-out: axiosInstance({ ..., meta: { clientLog: false } })
            const clientLogDisabled = cfg.meta && cfg.meta.clientLog === false;

            // skip if server already logged (your server sets this in central logger)
            const serverLogged = headers['x-server-error-logged'] === '1';

            if (!clientLogDisabled && !serverLogged) {
                const requestId =
                    headers['x-request-id'] ||
                    (typeof res?.data === 'object' && res?.data?.requestId) ||
                    cfg.headers?.['x-request-id'];

                const meta = {
                    axiosUrl: cfg.url,
                    method: cfg.method,
                    status: res?.status,
                    statusText: res?.statusText,
                    requestId,
                    timeout: cfg.timeout,
                    baseURL: cfg.baseURL,
                    // previews of what was sent (now smarter)
                    payload: summarize(cfg.data),
                    params: summarize(cfg.params),
                    headers: summarizeHeaders(cfg.headers),
                    // brief server body if available
                    serverMessage:
                        typeof res?.data === 'string'
                            ? clampStr(res.data, 300)
                            : clampStr(JSON.stringify(res?.data || {}), 300),
                };
                logError(err, 'axios error', meta);

                // mark so no other handler logs the same error object again
                try { Object.defineProperty(err, '__clientLogged', { value: true }); } catch { }

                var errorMessage = "Sorry, something went wrong. We're working to fix the issue."
                if (typeof window !== 'undefined' && window.$toast) {
                    window.$toast.error(errorMessage);
                }
            }

        } catch (e) {
            console.warn('axios logging failed', e);
        }

        return Promise.reject(err);
    }
);

// convenience wrapper (unchanged)
export function request(url, options = {}) {
    return axiosInstance({ url, ...options })
        .then(res => res.data)
        .catch(error => {
            const { response, request } = error || {};
            if (response) {
                error.uiCode = response.status;
                error.uiMessage = typeof response.data === 'string'
                    ? response.data
                    : (response.statusText || 'Server error');
            } else if (request) {
                error.uiCode = 'noResponse';
                error.uiMessage = 'No Response';
            } else {
                error.uiCode = 'setup';
                error.uiMessage = error.message;
            }


            throw error;
        });
}

export { axiosInstance };
